import { type ReactNode, useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useContextStore, activeContextStorage } from '@/lib/session/context-store';
import { useMyContexts, myContextsKey } from '@/lib/session/use-my-contexts';
import { useSwitchContext } from '@/lib/session/use-switch-context';
import { useOnboardingReadiness } from '@/lib/onboarding/use-onboarding-readiness';
import { useAgencyStatus } from '@/pages/Agency/useAgencyStatus';
import { useMyAgencyActivationState, agencyHeldPollIntervalMs } from '@/lib/hooks/useMyAgencyActivationState';
import { OnboardingReadinessScreen } from './OnboardingReadinessScreen';
import { AgencyPendingScreen } from '@/pages/Agency/AgencyPendingScreen';
import { AgencyActivationBoard } from '@/components/activation/AgencyActivationBoard';
import { BootLoader } from './BootLoader';

const PHASE1_POLL_MS = 3_000;
const PHASE1_TIMEOUT_MS = 15_000;

interface ReadinessGateProps {
  children: ReactNode;
}

/**
 * Two-phase gate for self-registered users whose tenant is still provisioning.
 *
 * Phase 1 — no membership yet: polls myContexts (gateway-exempt, safe pre-context)
 * every 3 s until a membership appears. IMPORTANT: store.memberships is populated by
 * ContextSwitcher, which is mounted in the Navbar alongside (not inside) this gate.
 * If ContextSwitcher is ever moved into ContextGate's subtree, it won't mount until
 * after this gate passes, and phase 1 will poll indefinitely.
 *
 * Transition: once a membership exists and no context is active, calls switchContext.
 * Prefers the tenant stored in localStorage (activeContextStorage) so multi-tenant
 * users land in their last-used workspace, not always memberships[0].
 * ContextGate (children) is not mounted yet, so ReadinessGate owns this call.
 * ContextGate's useContextBootstrap has an `if (active) return` guard that prevents
 * a double-switch when it eventually mounts.
 *
 * Agency fast-path — active context is AGENCY persona: bypass myOnboardingReadiness
 * (a CLIENT-provisioning resolver — agencies would get ACL_CONTEXT_REQUIRED or
 * ready:false forever). Instead check the platform-admin approval status via
 * myContexts (also gateway-exempt):
 *   - approved  → render the app
 *   - pending / declined → render the AgencyPendingScreen (waiting for admin action)
 * This is fail-CLOSED: an unknown or unresolved status is treated as pending.
 *
 * Phase 2 — context established (non-agency): polls myOnboardingReadiness until
 * ready: true. A 401 in this phase is a genuine auth failure (the context is set,
 * so the gateway should always accept the request) — surface it immediately,
 * do not re-poll.
 *
 * Stuck state: after ~60 s of polling with ready=false, isStuck flips true and the
 * screen presents honest copy plus two real actions: "Continue to app" (bypass) or
 * "Refresh". Bypass sets local `bypassed` state and renders children immediately —
 * polling stops. The BE may still resolve on its own; a page refresh will recheck.
 */
export function ReadinessGate({ children }: ReadinessGateProps) {
  const memberships = useContextStore((s) => s.memberships);
  const active = useContextStore((s) => s.active);
  const qc = useQueryClient();
  const [bypassed, setBypassed] = useState(false);
  const [phase1TimedOut, setPhase1TimedOut] = useState(false);

  const hasWorkspaces = memberships.length > 0;
  const hasActiveContext = active !== null;
  const isAgencyPersona = active?.persona === 'AGENCY';
  // SA (PLATFORM_ADMIN server-side, normalised to 'SA' by normalizePersona) users are
  // pre-provisioned system admins — they are never self-signup clients and
  // myOnboardingReadiness returns 401 for them (gateway requires a client provisioning context
  // that platform admins bypass by design). Skip Phase 2 entirely for this persona.
  const isSaPersona = active?.persona === 'SA';

  // Current pathname — used to scope the pending-agency agreement exception below.
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Phase 1: add a polling observer on the myContexts cache so that when
  // provisioning creates the first membership, the store update fires quickly.
  // myContexts is exempt from the gateway's tenant middleware — proven safe pre-context.
  const myCtxQuery = useMyContexts(undefined, { refetchInterval: !hasWorkspaces ? PHASE1_POLL_MS : false });
  // ?. is mock-compat: useMyContexts always returns a UseQueryResult in production, never undefined.
  const myContextsError = myCtxQuery?.isError ?? false;

  // Phase-1 timeout: if myContexts has not resolved in 15s, show the stuck screen.
  // useEffect dependency array ensures cleanup (clearTimeout) fires when Phase 1 exits.
  useEffect(() => {
    if (hasWorkspaces || hasActiveContext) return;
    const id = setTimeout(() => setPhase1TimedOut(true), PHASE1_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [hasWorkspaces, hasActiveContext]);

  // Transition: switch context when a membership appears.
  // Guard on isPending to avoid double-firing while the mutation is in flight.
  // suppressOverlay:true — this is a boot-time switch; WorkspaceSwitchOverlay must not fire.
  // Prefer the stored tenant (last-used restore) over memberships[0] so multi-tenant
  // users land in their previously active workspace.
  const switchCtx = useSwitchContext({ suppressOverlay: true });
  useEffect(() => {
    if (!hasWorkspaces || hasActiveContext || switchCtx.isPending || switchCtx.isError || !memberships[0]) return;
    const stored = activeContextStorage.get();
    const restore = stored ? memberships.find((m) => m.contextTenantId === stored) : undefined;
    switchCtx.mutate((restore ?? memberships[0]).contextTenantId);
  }, [hasWorkspaces, hasActiveContext, memberships, switchCtx]);

  // Agency approval status — enabled only for AGENCY persona to avoid an
  // unnecessary query for client/SA users. myContexts is gateway-exempt so this
  // is safe to call right after switchContext resolves (pre-full-context).
  const { approvalStatus, isLoading: approvalLoading } = useAgencyStatus({
    enabled: isAgencyPersona,
  });

  // Staged dashboard data — only needed for PENDING agencies.
  const {
    data: agencyActivationState,
    isLoading: activationLoading,
  } = useMyAgencyActivationState({
    enabled: isAgencyPersona && !approvalLoading && approvalStatus === 'pending',
    // ADR-015 rule 4: poll only while the board displays a hold (agreement `reason`, no NEEDED stage).
    refetchInterval: (query) => agencyHeldPollIntervalMs(query.state.data),
  });

  // Phase 2: poll readiness only once a context is active.
  // Disabled for agency persona — they take the fast-path above.
  // Disabled when bypassed — user has explicitly chosen to proceed.
  // 401 here is genuine — do NOT swallow it into another poll.
  const { ready, isLoading: readinessLoading, isError: readinessError, isStuck, refetch: refetchReadiness } = useOnboardingReadiness({
    // `active` is written only after switchContext succeeds, so it is the authoritative boot
    // signal. On a cold deep link the independently-hydrated membership list can briefly be empty
    // even though the server context is already confirmed; requiring both strands that route on
    // BootLoader until some unrelated navigation causes the contexts query to publish again.
    enabled: hasActiveContext && !bypassed && !isAgencyPersona && !isSaPersona,
  });

  // On ready, invalidate the contexts cache so any downstream observer (ContextGate,
  // ContextSwitcher) sees the provisioned workspace without waiting for staleTime.
  useEffect(() => {
    if (ready && hasActiveContext) {
      qc.invalidateQueries({ queryKey: myContextsKey() });
    }
  }, [ready, hasActiveContext, qc]);

  // Phase 1 or transition in progress. A confirmed active context wins over a temporarily empty
  // membership cache: it can only have been set by a successful server switch, and the contexts
  // query will continue hydrating independently for the switcher.
  if (!hasActiveContext) {
    // Error and timeout take priority over copy choice.
    if (myContextsError) {
      return <OnboardingReadinessScreen isError={true} />;
    }
    if (phase1TimedOut) {
      // No onContinue: without a membership the app cannot function.
      return <OnboardingReadinessScreen isError={false} isStuck={true} />;
    }
    // Show provisioning copy only when myContexts has resolved with confirmed empty memberships.
    // While loading (or cached data shows memberships), show the neutral spinner.
    const confirmedProvisioning =
      myCtxQuery?.isSuccess === true && (myCtxQuery?.data?.length ?? 1) === 0;
    if (confirmedProvisioning) return <OnboardingReadinessScreen isError={false} />;
    return <BootLoader />;
  }

  // Agency fast-path: route based on platform-admin approval status.
  // Fail-CLOSED: unknown/unresolved status → pending screen (never grants access).
  if (isAgencyPersona) {
    if (approvalLoading) return null;
    if (approvalStatus === 'approved') return <>{children}</>;
    if (approvalStatus === 'declined') {
      return (
        <div className="min-h-screen bg-bg text-text flex items-center justify-center p-6">
          <div className="w-full max-w-lg">
            <AgencyPendingScreen approvalStatus="declined" isLoading={false} />
          </div>
        </div>
      );
    }
    // PENDING: staged activation dashboard; fall back to flat screen while loading or on error.
    // Data-first: a failed background refetch keeps the last good state and sets `isError`, so the board is
    // kept whenever there IS data; the flat AgencyPendingScreen is only for a first read that failed (no data).
    if (agencyActivationState) {
      // Narrow exception: a pending agency may reach the exact agreement they are being
      // asked to sign. The actionId comes from the NEEDED stage in their own activation
      // state — the same value the CTA on the board navigates to — so this is the
      // minimum viable grant rather than a blanket /agreements/* pass-through.
      // A pending agency reaching any other route or any other agreement id still lands
      // on the board. The exception is inert when no NEEDED stage exists (no actionId).
      const pendingAgreementId =
        agencyActivationState.stages.find((st) => st.state === 'NEEDED' && st.actionId)
          ?.actionId ?? null;
      if (pendingAgreementId && pathname === `/agreements/${pendingAgreementId}`) {
        return <>{children}</>;
      }
      return (
        <div className="min-h-screen bg-bg text-text">
          <AgencyActivationBoard state={agencyActivationState} />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-bg text-text flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <AgencyPendingScreen approvalStatus="pending" isLoading={activationLoading} />
        </div>
      </div>
    );
  }

  // Platform admin fast-path: pre-provisioned system admins need no client provisioning check.
  if (isSaPersona) return <>{children}</>;

  // User chose to proceed despite readiness not confirming — render app directly.
  if (bypassed) return <>{children}</>;

  // Phase 2: genuine auth failure — surface it.
  if (readinessError) return <OnboardingReadinessScreen isError={true} />;

  // Phase 2: first query in flight — show spinner, not blank frame.
  if (readinessLoading) return <BootLoader />;

  // Phase 2: provisioning confirmed complete.
  if (ready) return <>{children}</>;

  // Phase 2: stuck — been waiting longer than "less than a minute" promises.
  // Give the user honest copy and a real escape.
  if (isStuck) {
    return (
      <OnboardingReadinessScreen
        isError={false}
        isStuck
        onContinue={() => setBypassed(true)}
      />
    );
  }

  // Phase 2: provisioning in progress — show loading screen while polling.
  // onCheckAgain re-polls status; it does NOT restart the provisioning saga.
  return <OnboardingReadinessScreen isError={false} onCheckAgain={() => void refetchReadiness()} />;
}
